/**
 * Vault app API client wiring.
 *
 * Constructs the authed client for the vault population and injects it into the
 * shared @secure-vault/api SDK via `configureApi()`. Unlike the advisor app, the
 * vault app has no org context (so no `X-Org-Id`), but it does carry a vault
 * session token once the owner/heir has unlocked their vault — that token is
 * surfaced to the SDK via `getVaultSessionToken`.
 */
import {
  API_BASE,
  configureApi,
  installRawBodyInterceptor,
} from "@secure-vault/api";
import { createClient, createConfig } from "@secure-vault/api/client-core";

import { auth } from "@/lib/firebase";
import { getVaultSessionToken } from "@/stores/vault-store";

async function buildAuthHeaders() {
  const u = auth.currentUser;
  if (!u) return {} as Record<string, string>;
  const token = await u.getIdToken();
  return { Authorization: `Bearer ${token}` } as Record<string, string>;
}

export const authedClient = createClient(
  createConfig({
    baseUrl: API_BASE,
  })
);

// Raw-body content-type handling for binary uploads (encrypted file ciphertext),
// shared with the advisor app.
installRawBodyInterceptor(authedClient);

export async function withAuthHeaders(extra?: Record<string, string>) {
  return { ...(await buildAuthHeaders()), ...(extra || {}) } as Record<
    string,
    string
  >;
}

configureApi({
  client: authedClient,
  getAuthHeaders: withAuthHeaders,
  getVaultSessionToken,
});
